import api from './api';
import { ApiResponse, PageResponse } from '../types/api';
import { ClienteRequestDTO, ClienteResponseDTO, ClienteSearchParams } from '../types/client';

const baseUrl = '/v1/clientes';

export const clientService = {
  async list(params?: ClienteSearchParams): Promise<ApiResponse<PageResponse<ClienteResponseDTO>>> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.page !== undefined) searchParams.append('page', String(params.page));
      if (params?.size !== undefined) searchParams.append('size', String(params.size));
      if (params?.sort) searchParams.append('sort', params.sort);
      if (params?.direction) searchParams.append('direction', params.direction);
      if (params?.filters?.nome) searchParams.append('nome', params.filters.nome);
      // Documento é opcional; se informado, encaminhamos como cpf/cnpj conforme tamanho
      const documento = params?.filters?.documento?.trim();
      if (documento) {
        const docClean = documento.replace(/[^\d]/g, '');
        if (docClean.length === 11) {
          searchParams.append('cpf', docClean);
        } else if (docClean.length === 14) {
          searchParams.append('cnpj', docClean);
        } else {
          // Se tamanho inválido, não envia documento para permitir listagem geral
        }
      }
      if (params?.filters?.ativo !== undefined) searchParams.append('ativo', String(params.filters.ativo));
      const qs = searchParams.toString();
      const url = qs ? `${baseUrl}?${qs}` : baseUrl;
      const response = await api.get<ApiResponse<PageResponse<ClienteResponseDTO>>>(url);
      return response.data as any;
    } catch (err) {
      // Fallback: se endpoint não existir, retornar estrutura vazia
      return {
        success: true,
        message: 'fallback',
        data: { content: [], totalElements: 0, totalPages: 0, size: params?.size || 10, number: params?.page || 0, first: true, last: true },
        timestamp: new Date().toISOString(),
      };
    }
  },

  async getById(id: string): Promise<ApiResponse<ClienteResponseDTO>> {
    const response = await api.get<ApiResponse<ClienteResponseDTO>>(`${baseUrl}/${id}`);
    return response.data as any;
  },

  // Busca cliente por documento. Se tiver 11 dígitos consulta CPF; se 14, CNPJ.
  async findByDocumento(documento: string): Promise<ApiResponse<ClienteResponseDTO>> {
    const clean = (documento || '').replace(/[^\d]/g, '');
    const isCPF = clean.length === 11;
    const isCNPJ = clean.length === 14;
    const url = isCNPJ ? `${baseUrl}/cnpj/${clean}` : `${baseUrl}/cpf/${clean}`;
    const response = await api.get<ApiResponse<ClienteResponseDTO>>(url);
    return response.data as any;
  },

  async create(data: ClienteRequestDTO): Promise<ApiResponse<ClienteResponseDTO>> {
    const clean = (data.documento || '').replace(/[^\d]/g, '');
    const payload: any = { nome: data.nome, email: data.email, telefone: data.telefone, endereco: data.endereco, ativo: data.ativo };
    if (clean.length === 11) payload.cpf = clean; else payload.cnpj = clean;
    const response = await api.post<ApiResponse<ClienteResponseDTO>>(baseUrl, payload);
    return response.data as any;
  },

  async update(id: string, data: ClienteRequestDTO): Promise<ApiResponse<ClienteResponseDTO>> {
    const clean = (data.documento || '').replace(/[^\d]/g, '');
    const payload: any = { nome: data.nome, email: data.email, telefone: data.telefone, endereco: data.endereco, ativo: data.ativo };
    if (clean.length === 11) payload.cpf = clean; else payload.cnpj = clean;
    const response = await api.put<ApiResponse<ClienteResponseDTO>>(`${baseUrl}/${id}`, payload);
    return response.data as any;
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    const response = await api.delete<ApiResponse<boolean>>(`${baseUrl}/${id}`);
    return response.data as any;
  },

  async activate(id: string): Promise<ApiResponse<ClienteResponseDTO>> {
    const response = await api.patch<ApiResponse<ClienteResponseDTO>>(`${baseUrl}/${id}/ativar`);
    return response.data as any;
  },

  async deactivate(id: string): Promise<ApiResponse<ClienteResponseDTO>> {
    const response = await api.patch<ApiResponse<ClienteResponseDTO>>(`${baseUrl}/${id}/desativar`);
    return response.data as any;
  },
};

export default clientService;